import { useState, useEffect, useCallback, memo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, AlertCircle } from 'lucide-react';
import type { StyleGuide } from '../types/research-definition';

interface StyleGuideSelectorProps {
  id?: string;
  value: string;
  onChange: (styleGuideId: string) => void;
}

export const StyleGuideSelector = memo<StyleGuideSelectorProps>(function StyleGuideSelector({
  id,
  value,
  onChange
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [styleGuides, setStyleGuides] = useState<StyleGuide[]>([]);

  const fetchStyleGuides = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fixed: Use correct endpoint /api/style-guides (not /api/outline/style-guides)
      const response = await fetch('/api/style-guides');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch style guides: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error('Failed to load style guides');
      }

      // Backend returns "styleGuides" not "guides"
      const guides = data.styleGuides || [];
      setStyleGuides(guides);

      // If current value is not in the list, reset to default
      if (guides.length > 0 && !guides.some((g: StyleGuide) => g.id === value)) {
        const defaultGuide = guides.find((g: StyleGuide) => g.id === 'linkedin_style_guidelines') || guides[0];
        if (defaultGuide) {
          onChange(defaultGuide.id);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error fetching style guides:', err);
      
      // Fallback to default if fetch fails
      if (value !== 'linkedin_style_guidelines') {
        onChange('linkedin_style_guidelines');
      }
    } finally {
      setIsLoading(false);
    }
  }, [onChange, value]);

  useEffect(() => {
    fetchStyleGuides();
  }, [fetchStyleGuides]);

  const handleValueChange = useCallback((newValue: string) => {
    onChange(newValue);
  }, [onChange]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="h-4 w-4" />
        <span className="text-sm">Error loading guides</span>
      </div>
    );
  }

  if (styleGuides.length === 0) {
    return (
      <span className="text-sm text-muted-foreground">No writing styles available</span>
    );
  }

  return (
    <Select value={value} onValueChange={handleValueChange}>
      <SelectTrigger id={id} className="w-full">
        <SelectValue placeholder="Select writing style" />
      </SelectTrigger>
      <SelectContent>
        {styleGuides.map((guide) => (
          <SelectItem key={guide.id} value={guide.id}>
            {(guide as any).name || guide.label || guide.id}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
});
